import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type {
  CpmReportCampaignQuery, CpmReportCampaignQueryVariables, CpmReportSpendQuery, CpmReportSpendQueryVariables,
  CpmReportSubmissionsQuery, CpmReportSubmissionsQueryVariables,
} from '@/generated/cpm-report';
import { CPM_REPORT_CAMPAIGN, CPM_REPORT_SPEND, CPM_REPORT_SUBMISSIONS } from '@/queries/cpm-report';
import { buildCampaignReport, reportTimestamp, safeInteger, validateCampaign } from './model';
import { CampaignReportError, type ReportCampaign, type ReportSpendDay, type ReportSubmission } from './types';

export type ReportClient = Pick<ApolloClient<NormalizedCacheObject>, 'query'>;
export type ReportProgress = { loaded: number; total: number };
const PAGE_SIZE = 100;

function ensureActive(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Report cancelled', 'AbortError');
}
function fingerprint(campaign: ReportCampaign) {
  return JSON.stringify(Object.keys(campaign).filter((key) => key !== '__typename').sort()
    .map((key) => [key, campaign[key as keyof ReportCampaign]]));
}

/** A guarded collection interval, not a database snapshot or a provider refresh. */
export async function loadCampaignReport(
  client: ReportClient,
  campaignId: string,
  signal: AbortSignal,
  onProgress?: (progress: ReportProgress) => void,
) {
  ensureActive(signal);
  const collectionStartedAt = new Date().toISOString();
  const requestOptions = {
    fetchPolicy: 'no-cache' as const, errorPolicy: 'all' as const,
    context: { fetchOptions: { signal }, queryDeduplication: false },
  };
  const readCampaign = async () => {
    ensureActive(signal);
    const result = await client.query<CpmReportCampaignQuery, CpmReportCampaignQueryVariables>({
      query: CPM_REPORT_CAMPAIGN, variables: { id: campaignId }, ...requestOptions,
    });
    ensureActive(signal);
    if (result.errors?.length || ('error' in result && result.error) || !result.data?.adminCpmCampaign) throw new CampaignReportError('load');
    const campaign = result.data.adminCpmCampaign;
    validateCampaign(campaign);
    if (campaign.id !== campaignId) throw new CampaignReportError('inconsistent');
    return campaign;
  };
  const campaign = await readCampaign();
  const total = campaign.submissions_count;
  onProgress?.({ loaded: 0, total });
  const submissions: ReportSubmission[] = [];
  const seen = new Set<string>();
  let previousCreatedAt = Infinity;
  do {
    ensureActive(signal);
    const result = await client.query<CpmReportSubmissionsQuery, CpmReportSubmissionsQueryVariables>({
      query: CPM_REPORT_SUBMISSIONS,
      variables: { campaignId, limit: PAGE_SIZE, offset: submissions.length }, ...requestOptions,
    });
    ensureActive(signal);
    if (result.errors?.length || ('error' in result && result.error) || !result.data?.adminCpmCampaignSubmissions) throw new CampaignReportError('load');
    const page = result.data.adminCpmCampaignSubmissions;
    safeInteger(page.total);
    safeInteger(page.pageCount);
    if (page.total !== total || page.pageCount !== Math.max(1, Math.ceil(total / PAGE_SIZE))
      || !Array.isArray(page.data) || page.data.length !== Math.min(PAGE_SIZE, total - submissions.length)) {
      throw new CampaignReportError('inconsistent');
    }
    for (const row of page.data) {
      if (!row || typeof row.id !== 'string' || !row.id.trim()) throw new CampaignReportError('invalid');
      const createdAt = reportTimestamp(row.createdAt);
      if (seen.has(row.id) || createdAt > previousCreatedAt) throw new CampaignReportError('inconsistent');
      seen.add(row.id);
      previousCreatedAt = createdAt;
      submissions.push(row);
    }
    onProgress?.({ loaded: submissions.length, total });
  } while (submissions.length < total);
  ensureActive(signal);
  const spend = await client.query<CpmReportSpendQuery, CpmReportSpendQueryVariables>({
    query: CPM_REPORT_SPEND, variables: { campaignId }, ...requestOptions,
  });
  ensureActive(signal);
  const stats = spend.data?.adminCpmCampaignSubmissions?.stats;
  if (spend.errors?.length || ('error' in spend && spend.error) || !stats || !Array.isArray(stats.daily_spend)) {
    throw new CampaignReportError('load');
  }
  const dailySpend = stats.daily_spend.map((point) => ({ date: point?.date, spend_cents: point?.spend_cents })) as ReportSpendDay[];
  if (dailySpend.reduce((sum, point) => sum + safeInteger(point.spend_cents), 0) !== safeInteger(stats.total_earnings_cents)) {
    throw new CampaignReportError('inconsistent');
  }
  const after = await readCampaign();
  if (fingerprint(campaign) !== fingerprint(after)) throw new CampaignReportError('inconsistent');
  ensureActive(signal);
  return buildCampaignReport({ campaign, submissions, dailySpend, collectionStartedAt, collectionFinishedAt: new Date().toISOString() });
}
